'use client';
import React, { useState, useEffect, useRef } from 'react';

const clamp = (v, min, max) => Math.max(min, Math.min(v, max));
const lerp = (a, b, t) => a + (b - a) * t;

const ScrollFollowingTimeline = () => {
  const [activeYearIndex, setActiveYearIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const sectionRef = useRef(null);
  const yearsWrapRef = useRef(null);
  const yearRefs = useRef([]);
  const animRef = useRef(null);
  const targetYRef = useRef(0);
  const currentYRef = useRef(0);

  const PADDING_TOP = 80;
  const PADDING_BOTTOM = 80;

  const historyData = [
    {
      year: 2025,
      events: [
        {
          month: '10',
          title: ['“㈜케이디다츠” 사명 변경'],
        },
        {
          month: '09',
          title: [
            '국방과학연구소 민군협력진흥원 민군겸용기술적용연구사업 계약',
          ],
        },
        {
          month: '03',
          title: [
            '”㈜코리아디펜스인더스트리”와 지분양도 계약 체결, 자회사 편입',
          ],
        },
      ],
    },
    {
      year: 2024,
      events: [
        {
          month: '07',
          title: ['대드론 무인기용 영상탐색기 납품 계약 체결'],
        },
      ],
    },
    {
      year: 2023,
      events: [
        {
          month: '02',
          title: [
            'LIG넥스원 “동시감시정찰을 위한 무인기 군집제어 기술 개발 과제” 계약 체결',
          ],
        },
      ],
    },
    {
      year: 2022,
      events: [
        {
          month: '12',
          title: [
            '국방기술진흥연구소 대전방산혁신클러스트 드론특화  개발비 지원사업 계약 체결',
          ],
        },
        {
          month: '09',
          title: ['중소벤처기업부 중소기업기술개발지원사업 TIPS과제 협약 체결'],
        },
        {
          month: '04',
          title: ['국방기술진흥연구소 국방벤처협약기업 선정'],
        },
      ],
    },
    {
      year: 2021,
      // award: 'Company Foundation Year',
      // logo: '/images/guinness-logo.png',
      events: [
        {
          month: '12',
          title: '혁신기업국가대표 1000 방산부문 선정',
        },
      ],
    },
    {
      year: 2020,
      // award: 'Company Foundation Year',
      // logo: '/images/foundation-logo.png',
      events: [
        {
          month: '12',
          title: '방위사업청 신속획득시범사업 계약 체결',
        },

        { month: '02', title: '벤처기업 인증' },
      ],
    },
    {
      year: 2019,
      // award: 'Company Foundation Year',
      logo: '/images/foundation-logo.png',
      events: [
        { month: '09', title: '기업부설연구소 설립' },
        { month: '08', title: '회사 설립' },
      ],
    },
  ];

  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    if (!sectionRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        setVisible(e.isIntersecting);
      },
      {
        root: null,
        threshold: 0.1,
      },
    );
    io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const handle = () => {
      if (!sectionRef.current || !yearsWrapRef.current) return;

      const wrapRect = yearsWrapRef.current.getBoundingClientRect();
      const winH = window.innerHeight;

      let closestIndex = 0;
      let closestDist = Infinity;
      yearRefs.current.forEach((el, idx) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const elCenter = r.top + r.height / 2;
        const screenCenter = winH / 2;
        const d = Math.abs(elCenter - screenCenter);
        if (d < closestDist) {
          closestDist = d;
          closestIndex = idx;
        }
      });
      setActiveYearIndex(closestIndex);

      const activeEl = yearRefs.current[closestIndex];
      if (!activeEl) return;

      const activeRect = activeEl.getBoundingClientRect();
      const targetInWrap =
        activeRect.top + activeRect.height / 2 - wrapRect.top;

      const minY = PADDING_TOP;
      const maxY = wrapRect.height - PADDING_BOTTOM;

      targetYRef.current = clamp(targetInWrap, minY, maxY);
      if (!animRef.current) {
        animRef.current = requestAnimationFrame(tick);
      }
    };

    const onScroll = () => handle();
    const onResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
      handle();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    handle();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const [indicatorY, setIndicatorY] = useState(0);
  const tick = () => {
    currentYRef.current = lerp(currentYRef.current, targetYRef.current, 0.18);
    setIndicatorY(currentYRef.current);

    if (Math.abs(currentYRef.current - targetYRef.current) < 0.5) {
      currentYRef.current = targetYRef.current;
      setIndicatorY(currentYRef.current);
      animRef.current = null;
    } else {
      animRef.current = requestAnimationFrame(tick);
    }
  };

  useEffect(() => {
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="px-4 pt-24 w-full pb-32 mx-auto bg-gray-950 sm:px-6 lg:px-8 lg:pt-40 min-h-screen bg-[url('/images/mapBG.svg')] bg-no-repeat bg-center"
    >
      <div className="lg:px-36 mx-auto">
        <div className="mb-12 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-100 mb-4">
            Company History
          </h2>
        </div>

        {/* 상대 위치 기준 컨테이너 */}
        <div className="relative" ref={yearsWrapRef}>
          {/* 세로선 */}
          <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-1 bg-blue-600 lg:-translate-x-1/2 z-0" />

          {/* 움직이는 인디케이터 */}
          <div
            className={[
              'absolute z-20 w-6 h-6 lg:w-8 lg:h-8 rounded-full border-4 border-white shadow-lg pointer-events-none',
              visible ? 'opacity-100' : 'opacity-0',
              'transition-opacity duration-300 ease-out',
              'bg-blue-600',
              '-translate-x-1/2',
            ].join(' ')}
            style={{
              left: isLargeScreen ? '50%' : '16px',
              top: indicatorY,
            }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <svg
                className="w-2 h-2 lg:w-3 lg:h-3 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>

          {/* 연도별 컨텐츠 */}
          <div className="space-y-16 lg:space-y-32">
            {historyData.map((yearData, yearIndex) => (
              <div
                key={yearData.year}
                ref={(el) => (yearRefs.current[yearIndex] = el)}
                className="relative"
              >
                {/* 데스크톱 레이아웃 */}
                <div className="hidden lg:block">
                  <div className="grid grid-cols-12 gap-8 items-start">
                    {/* 왼쪽: 연도/어워드 */}
                    <div className="col-span-5 text-right pr-8">
                      <div className="sticky top-32">
                        <h3
                          className={[
                            'text-8xl font-bold mb-4',
                            activeYearIndex === yearIndex
                              ? 'text-blue-600'
                              : 'text-gray-300',
                            'transition-colors duration-300',
                          ].join(' ')}
                        >
                          {yearData.year}
                        </h3>
                        <div
                          className={`font-bold text-lg mb-4 uppercase tracking-wide transition-colors duration-300 ${
                            activeYearIndex === yearIndex
                              ? 'text-blue-600'
                              : 'text-gray-300'
                          }`}
                        >
                          {yearData.award}
                        </div>
                      </div>
                    </div>

                    {/* 중앙 공백 */}
                    <div className="col-span-2" />

                    {/* 오른쪽: 월별 이벤트 */}
                    <div className="col-span-5 pl-8">
                      <div className="space-y-6">
                        {yearData.events.map((event, eventIndex) => (
                          <div
                            key={eventIndex}
                            className="flex items-start space-x-6"
                          >
                            <div className="flex-shrink-0">
                              <div className="w-12 h-8 flex items-center justify-center font-black text-white">
                                {event.month}
                              </div>
                            </div>
                            <div className="flex-1">
                              <p className="text-gray-100 text-lg leading-relaxed font-medium">
                                {event.title}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 모바일 레이아웃 */}
                <div className="lg:hidden ml-10 sm:ml-12">
                  {/* 연도와 어워드 */}
                  <div className="mb-6">
                    <h3
                      className={[
                        'text-4xl sm:text-5xl font-bold mb-2',
                        activeYearIndex === yearIndex
                          ? 'text-blue-600'
                          : 'text-gray-300',
                        'transition-colors duration-300',
                      ].join(' ')}
                    >
                      {yearData.year}
                    </h3>
                    {yearData.award && (
                      <div
                        className={`text-xs sm:text-sm font-bold mb-4 uppercase tracking-wide transition-colors duration-300 ${
                          activeYearIndex === yearIndex
                            ? 'text-blue-600'
                            : 'text-gray-400'
                        }`}
                      >
                        {yearData.award}
                      </div>
                    )}
                  </div>

                  {/* 이벤트 목록 */}
                  <div className="space-y-5 sm:space-y-6">
                    {yearData.events.map((event, eventIndex) => (
                      <div
                        key={eventIndex}
                        className="flex items-start space-x-3 sm:space-x-4"
                      >
                        {event.month && (
                          <div className="flex-shrink-0">
                            <div className="min-w-[2.5rem] sm:min-w-[3rem] h-7 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-black text-blue-400">
                              {event.month}
                            </div>
                          </div>
                        )}
                        <div className="flex-1 pt-1">
                          <p className="text-gray-100 text-sm sm:text-base leading-relaxed font-medium">
                            {event.title}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ScrollFollowingTimeline;
